import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Link,
  TextField,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { login } from "../api/Authentication/api";
import { AuthContext } from "../contexts/AuthContext";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { isAuthenticated, setUserDetails } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await login(email, password);

      if (response?.data?.access && response?.data?.refresh) {
        // Pass tokens to context which will handle user data fetching
        await setUserDetails(response.data);
      } else {
        setError("Invalid response from server");
      }
    } catch (err) {
      setError(err.error)
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#A8CE97", // Light green background
      }}
    >
      <Container maxWidth="xs">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Box display="flex" justifyContent="left" mb={3}>
            <img src="/logo.svg" alt="Logo" style={{ height: 40 }} />
          </Box>
          <Box component="form" onSubmit={handleSubmit}>
            <Typography
              variant="h5"
              align="left"
              fontWeight="bold"
              gutterBottom
              sx={{ color: "#124F3E" }} // Dark green text color
            >
              Login
            </Typography>
            {error && (
              <Typography color="error" align="center" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}
            <TextField
              fullWidth
              label="Email"
              type="email"
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Typography align="right" variant="body2" sx={{ mt: 1 }}>
              <Link
                component={RouterLink}
                to="/forgot-password"
                sx={{ color: "#124F3E" }}
              >
                Forgot Password ?
              </Link>
            </Typography>
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mt: 0 }}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityIcon />
                      ) : (
                        <VisibilityOffIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                bgcolor: "#124F3E",
                ":hover": { bgcolor: "#0E3D30" },
              }}
            >
              Login
            </Button>
            <Typography align="center" variant="body2">
              Don't have an account?{" "}
              <Link
                component={RouterLink}
                to="/signup"
                sx={{ color: "#124F3E" }}
              >
                Sign up
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
